import { SyncStatus } from "../../Resources/Constants";
import { makeAutoObservable } from "mobx";
import NetworkManager from "./NetworkManager";

export class CoachSyncStatusManager {
  hasChangesOffline = false;
  networkManager = NetworkManager.getInstance();

  operations = {
    performTransaction: null,

    listenerClients: null,
    listenerInvitations: null,
    listenerChats: null,

    fetchCoach: null,
    fetchCoachPublic: null,
    fetchForms: null,
    fetchFutureCompetitions: null,
    fetchPastCompetitions: null,
    fetchBlockLibrary: null,
    fetchWorkoutLibrary: null,
    // fetchTeamCoaches: null,

    fetchFoodLibrary: null,
    fetchNutritionPlans: null,

    fetchApplicationForm: null,
    fetchApplications: null,
    fetchSubscriptions: null,
    fetchLogo: null,
  };

  constructor(coach) {
    this.coach = coach;
    makeAutoObservable(this);
  }

  startOperation(name) {
    if (!(name in this.operations)) {
      console.warn(`Operation "${name}" is not a recognized operation.`);
      return;
    }
    this.operations[name] = SyncStatus.WAITING; // use constant for consistency
    console.log("Operation started:", name);
  }

  /** Mark an individual operation as completed (success or failure) */
  endOperationSuccess(name) {
    if (!(name in this.operations)) {
      console.warn(`Operation "${name}" is not a recognized operation.`);
      return;
    }
    this.operations[name] = SyncStatus.SYNCED;
  }

  endOperationError(name) {
    if (!(name in this.operations)) {
      console.warn(`Operation "${name}" is not a recognized operation.`);
      return;
    }
    this.operations[name] = SyncStatus.ERROR;
  }

  get syncStatus() {
    if (
      Object.values(this.operations).includes(SyncStatus.ERROR) ||
      this.coach.clients.some(
        (client) => client.syncStatusManager.syncStatus === SyncStatus.ERROR
      )
    ) {
      return SyncStatus.ERROR;
    } else if (
      Object.values(this.operations).includes(SyncStatus.WAITING) ||
      this.coach.clients.some(
        (client) => client.syncStatusManager.syncStatus === SyncStatus.WAITING
      )
    ) {
      return SyncStatus.WAITING;
    } else {
      return SyncStatus.SYNCED;
    }
  }

  /** Get failed operations for retry */
  getFailedOperations() {
    return Object.entries(this.operations)
      .filter(([_, status]) => status === SyncStatus.ERROR)
      .map(([key]) => key);
  }

  async resolveErrors() {
    console.log("Executing resolveErrors...");

    const operationHandlers = {
      listenerClients: async () => {
        await this.coach.firestoreListener.setupClientsListener(this.coach);
      },

      listenerInvitations: async () => {
        await this.coach.firestoreListener.setupInvitationsListener(this.coach);
      },

      listenerChats: async () => {
        await this.coach.firestoreListener.setupChatsListener(this.coach);
      },

      fetchCoach: async () => {
        await this.coach.fetchCoachFromCloud(
          this.coach.id,
          this.coach.name,
          this.coach.email
        );
      },
      fetchCoachPublic: async () => {
        await this.coach.fetchCoachPublicFromCloud();
      },
      fetchForms: async () => {
        if (
          this.coach.coachTransactionForms.length === 0 &&
          this.coach.coachDeletionForms.length === 0
        ) {
          this.coach.setProperty("forms", []);
          await this.coach.fetchFormsFromCloud();
        }
      },

      fetchFutureCompetitions: async () => {
        if (
          this.coach.coachTransactionCompetitions.length === 0 &&
          this.coach.coachDeletionCompetitions.length === 0
        ) {
          const today = new Date();

          const filteredCompetitions = this.coach.competitions.filter(
            (comp) => new Date(comp.date) < today
          );

          this.coach.setProperty("competitions", filteredCompetitions);

          await this.coach.fetchCompetitionsFromCloud(true);
        }
      },

      fetchPastCompetitions: async () => {
        if (
          this.coach.coachTransactionCompetitions.length === 0 &&
          this.coach.coachDeletionCompetitions.length === 0
        ) {
          const today = new Date();

          const filteredCompetitions = this.coach.competitions.filter(
            (comp) => new Date(comp.date) >= today
          );

          this.coach.setProperty("competitions", filteredCompetitions);

          await this.coach.fetchCompetitionsFromCloud(false);
        }
      },

      //   fetchTeamCoaches: async () => {
      //     this.coach.fetchTeamCoachesFromCloud()
      //   },

      fetchBlockLibrary: async () => {
        if (
          this.coach.coachTransactionBlocks.length === 0 &&
          this.coach.coachDeletionBlocks.length === 0
        ) {
          this.coach.setProperty("blockLibrary", []);
          await this.coach.fetchBlockLibraryFromCloud();
        }
      },
      fetchWorkoutLibrary: async () => {
        if (
          this.coach.coachTransactionWorkouts.length === 0 &&
          this.coach.coachDeletionWorkouts.length === 0
        ) {
          this.coach.setProperty("workoutLibrary", []);
          await this.coach.fetchWorkoutLibraryFromCloud();
        }
      },
      fetchFoodLibrary: async () => {
        if (
          this.coach.coachTransactionFoods.length === 0 &&
          this.coach.coachDeletionFoods.length === 0
        ) {
          this.coach.setProperty("foods", []);
          await this.coach.fetchFoodLibraryFromCloud();
        }
      },
      fetchNutritionPlans: async () => {
        if (
          this.coach.coachTransactionNutritionPlans.length === 0 &&
          this.coach.coachDeletionNutritionPlans.length === 0
        ) {
          this.coach.setProperty("nutritionPlans", []);
          await this.coach.fetchNutritionPlansFromCloud();
        }
      },
      fetchApplicationForm: async () => {
        await this.coach.fetchApplicationFormFromCloud();
      },
      fetchApplications: async () => {
        await this.coach.fetchApplicationsFromCloud();
      },
      fetchSubscriptions: async () => {
        await this.coach.fetchSubscriptionFromCloud();
      },
      fetchLogo: async () => {
        await this.coach.fetchLogoFromCloud();
      },
      performTransaction: async () => {
        await this.coach.performTransaction();
      },
    };

    for (const operation of this.getFailedOperations()) {
      console.log(`Retrying ${operation}...`);

      if (operationHandlers[operation]) {
        await this.safeOperation(operation, operationHandlers[operation], true);
      } else {
        console.warn(`No handler found for operation: ${operation}`);
      }
    }

    const clientsWithErrors = this.coach.clients.filter(
      (client) => client.syncStatusManager.syncStatus === SyncStatus.ERROR
    );
    console.log(`There are ${clientsWithErrors.length} clients with errors.`);
    clientsWithErrors.forEach((c) => c.syncStatusManager.resolveErrors());
  }

  getErrorToastContent(operationName) {
    const errorMessages = {
      performTransaction: {
        title: "syncErrorTransaction-string",
        description: "syncErrorTransactionDescription-string",
      },
      listenerClients: {
        title: "syncErrorClients-string",
        description: "syncErrorClientsDescription-string",
      },
      listenerInvitations: {
        title: "syncErrorInvitations-string",
        description: "syncErrorInvitationsDescription-string",
      },
      listenerChats: {
        title: "syncErrorChats-string",
        description: "syncErrorChatsDescription-string",
      },
      fetchCoach: {
        title: "syncErrorCoachData-string",
        description: "syncErrorCoachDataDescription-string",
      },
      fetchCoachPublic: {
        title: "syncErrorCoachProfile-string",
        description: "syncErrorCoachProfileDescription-string",
      },
      fetchForms: {
        title: "syncErrorForms-string",
        description: "syncErrorFormsDescription-string",
      },
      fetchFutureCompetitions: {
        title: "syncErrorCompetitions-string",
        description: "syncErrorCompetitionsDescription-string",
      },
      fetchPastCompetitions: {
        title: "syncErrorCompetitions-string",
        description: "syncErrorCompetitionsDescription-string",
      },
      fetchBlockLibrary: {
        title: "syncErrorBlockLibrary-string",
        description: "syncErrorBlockLibraryDescription-string",
      },
      fetchWorkoutLibrary: {
        title: "syncErrorWorkoutLibrary-string",
        description: "syncErrorWorkoutLibraryDescription-string",
      },
      fetchFoodLibrary: {
        title: "syncErrorFoodLibrary-string",
        description: "syncErrorFoodLibraryDescription-string",
      },
      fetchNutritionPlans: {
        title: "syncErrorNutritionPlans-string",
        description: "syncErrorNutritionPlansDescription-string",
      },
      fetchApplicationForm: {
        title: "syncErrorApplicationForm-string",
        description: "syncErrorApplicationFormDescription-string",
      },
      fetchApplications: {
        title: "syncErrorApplications-string",
        description: "syncErrorApplicationsDescription-string",
      },
      fetchSubscriptions: {
        title: "syncErrorSubscription-string",
        description: "syncErrorSubscriptionDescription-string",
      },
      fetchLogo: {
        title: "syncErrorLogo-string",
        description: "syncErrorLogoDescription-string",
      },
    };
    const content = errorMessages[operationName] || {
      title: "syncError-string",
      description: "syncErrorGenericDescription-string",
    };

    return {
      ...content,
      variables: {
        coachEmail: this.coach?.email || "unknown",
      },
    };
  }

  async safeOperation(operationName, operationFn, resolvingErrors = false) {
    if (
      this.operations[operationName] === SyncStatus.SYNCED &&
      !operationName.toLowerCase().includes("listener") &&
      !["performTransaction"].includes(operationName)
    ) {
      console.log(`${operationName} is already synced. Skipping execution.`);
      return;
    }

    if (this.networkManager.isOffline) {
      console.log(
        `Cannot execute ${operationName} while offline. Skipping execution.`
      );
      return;
    }

    try {
      this.startOperation(operationName);
      const result = await operationFn();
      this.endOperationSuccess(operationName);
      return result;
    } catch (error) {
      console.error(`${operationName} failed:`, error);
      //   crashlytics().recordError(error);

      // Show error toast if showToast is available
      if (
        this.showToast &&
        typeof this.showToast === "function" &&
        (resolvingErrors ||
          ["fetchApplicationForm", "fetchApplications"].includes(operationName))
      ) {
        const toastContent = this.getErrorToastContent(operationName);
        this.showToast(toastContent.title, {
          type: "error",
          description: toastContent.description,
        });
      }

      this.endOperationError(operationName);
      return;
    }
  }

  setOperationStatus(name, status) {
    if (!(name in this.operations)) {
      console.warn(`Operation "${name}" is not a recognized operation.`);
      return;
    }
    this.operations[name] = status;
    // this.updateGlobalStatus();
  }

  resetAllOperations() {
    for (const key in this.operations) {
      if (Object.hasOwnProperty.call(this.operations, key)) {
        this.operations[key] = null;
      }
    }
    console.log("All operations reset to SYNCED");
  }

  get showToast() {
    return this.coach.showToast;
  }

  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
      console.log(`Property ${propertyName} set to ${value}`);
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }
}
